import { ProvidersContactsLocaleEnumType } from '__generated__/types';
import { EMessengerEventBusEvents, useMonitoringClient } from '@xometry/ui';
import { amplitudeLogger, LOGOUT_CLICK } from 'common/amplitude';
import { LocaleEnum } from 'common/config/locales';
import { getRegionDefaultLocale, getRegionSupportedLocales } from 'common/config/regions/utils';
import { HEADER_EVENTS } from 'common/enums/amplitudeEvents';
import { DeviceTypes } from 'common/enums/deviceTypes';
import { useDeviceTypeByWindowSize } from 'common/hooks/useDeviceType';
import { useMessengerEventBus } from 'common/hooks/useMessengerEventBus';
import { useRouteQuery } from 'common/hooks/useRouteQuery/useRouteQuery';
import { clearAuthToken, clearRefreshToken, redirectToRoot } from 'common/requests';
import { HeaderLogo } from 'components/Header/HeaderLogo';
import { MessengerHeaderButton } from 'components/Messenger/MessengerHeaderButton/MessengerHeaderButton';
import { noop } from 'es-toolkit';
import { useContactsSignOutMutation } from 'graphql/mutations/__generated__/contactsSignOut';
import { useContactsUpdateMutation } from 'graphql/mutations/__generated__/contactsUpdate';
import { ContactType } from 'graphql/queries/types';
import qs from 'qs';
import { FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { BurgerMenuWrapper } from './BurgerMenuWrapper/BurgerMenuWrapper';
import { HeaderMenu } from './HeaderMenu';
import styles from './PrivateHeader.module.less';

interface AuthHeaderProps {
  contact: ContactType;
}

const DTI = 'Header';

export const PrivateHeader: FC<AuthHeaderProps> = ({ contact }) => {
  const [userSignOut] = useContactsSignOutMutation();
  const deviceType = useDeviceTypeByWindowSize();
  const isDesktop = deviceType === DeviceTypes.DESKTOP;
  const { i18n } = useTranslation();
  const history = useHistory();
  const { sendEvent } = useMessengerEventBus();
  const query = useRouteQuery();
  const [updateContact] = useContactsUpdateMutation();

  const isSigningOut = useRef(false);

  const logout = async () => {
    // A second tap would send a second sign-out that the redirect below aborts.
    if (isSigningOut.current) return;

    isSigningOut.current = true;
    amplitudeLogger(LOGOUT_CLICK);
    // The session ends here even when the request does not get through (e.g. WebKit's "Load failed");
    // an HTTP or GraphQL error is already reported by the Apollo error link.
    await userSignOut().catch(noop);
    clearAuthToken();
    clearRefreshToken();
    redirectToRoot();
  };

  const handleChangeLocale = useCallback(
    async (newLang: LocaleEnum | string) => {
      const currentSearch = qs.parse(location.search.replace('?', ''));
      const currentLocaleInUrl = currentSearch.locale;
      await updateContact({
        variables: { contact: { id: contact.id, locale: newLang as ProvidersContactsLocaleEnumType } },
      });
      // Awaited: the URL write below re-runs the sync effect, which must already see the new language.
      await i18n.changeLanguage(newLang);
      localStorage.setItem('currentLanguage', newLang);

      if (currentLocaleInUrl === newLang) return;

      const updatedSearch = `?${qs.stringify({ ...currentSearch, locale: newLang })}`;

      history.push({ search: updatedSearch });

      sendEvent?.(EMessengerEventBusEvents.CHANGE_LANGUAGE, { newLocale: newLang });

      amplitudeLogger(HEADER_EVENTS.LANGUAGE_CHANGED, { new_language: newLang });
    },
    [contact.id, history, i18n, sendEvent, updateContact],
  );

  useEffect(() => {
    const currentLang = i18n.language;
    // The URL and localStorage are user-editable: an unsupported value (e.g. `?locale=UK`) must not reach `contactsUpdate`.
    const supportedLocales: string[] = getRegionSupportedLocales();
    const toSupportedLocale = (locale: string | null) => (locale && supportedLocales.includes(locale) ? locale : null);
    const urlLocale = toSupportedLocale(query.get('locale'));
    const storedLocale = toSupportedLocale(localStorage.getItem('currentLanguage'));
    const fallbackLocale = getRegionDefaultLocale();

    const effectiveLocale = contact.locale || urlLocale || storedLocale || fallbackLocale;

    if (effectiveLocale && effectiveLocale !== currentLang) {
      // A failed save is already reported and shown by the Apollo error link.
      handleChangeLocale(effectiveLocale).catch(noop);

      return;
    }

    if (!urlLocale && currentLang) {
      const data = qs.parse(location.search.replace('?', ''));
      history.replace({
        search: `?${qs.stringify({ ...data, locale: currentLang })}`,
      });
    }
  }, [handleChangeLocale, query, i18n.language, history, contact.locale]);

  const monitoringClient = useMonitoringClient();

  useEffect(() => {
    if (contact?.id) {
      monitoringClient.setClientId(String(contact.id));
      monitoringClient.setUserId(String(contact.id));
    }
  }, [contact?.id, monitoringClient]);

  return (
    <header data-testid={DTI} className={styles.container}>
      {!isDesktop ? (
        <BurgerMenuWrapper>
          {() => (
            <HeaderMenu
              data-testid={`${DTI}__Menu`}
              contact={contact}
              onLogout={() => void logout()}
              onLocaleChange={handleChangeLocale}
            />
          )}
        </BurgerMenuWrapper>
      ) : null}
      <HeaderLogo data-testid={`${DTI}__Logo`} />
      {!isDesktop ? (
        <div className={styles.messengerBtn}>
          <MessengerHeaderButton className={styles.mobileMessengerBtn} />
        </div>
      ) : (
        <HeaderMenu
          data-testid={`${DTI}__Menu`}
          contact={contact}
          onLogout={() => void logout()}
          onLocaleChange={handleChangeLocale}
        />
      )}
    </header>
  );
};
